import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, CheckCircle2, Calendar, Download, Wrench } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { api, API } from "@/lib/api";
import { downloadCsv } from "@/lib/csv";
import { Th, Td, StatusBadge } from "@/components/admin/shared/UIPrimitives";
import ManualBookingDialog from "@/components/admin/ManualBookingDialog";
import RecurringPatternsPanel from "@/components/admin/RecurringPatternsPanel";

export default function BookingsTable({ bookings, refetch, gcalConnected, onEdit }) {
  const [confirming, setConfirming] = useState(null);
  const [addingManual, setAddingManual] = useState(false);

  const backfillInvoices = async () => {
    // First: dry run so admin sees what will change
    try {
      const { data: preview } = await api.post("/admin/maintenance/backfill-booking-invoices");
      if (preview.count === 0) {
        toast.success("Nothing to backfill — all confirmed manual bookings already have invoices.");
        return;
      }
      const names = preview.bookings.map((b) => b.student_name).join(", ");
      const ok = window.confirm(
        `Backfill invoice numbers for ${preview.count} booking(s)?\n\n` +
        `Students: ${names}\n\n` +
        `This is safe and idempotent — it only assigns invoice numbers to confirmed bookings that don't have one yet.`
      );
      if (!ok) return;
      const { data: applied } = await api.post("/admin/maintenance/backfill-booking-invoices?apply=true");
      toast.success(`Backfilled ${applied.count} invoice${applied.count > 1 ? "s" : ""} — refresh to see them.`);
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    }
  };

  const decline = async (b) => {
    if (!confirm(`Decline ${b.parent_name}'s booking request?`)) return;
    try {
      await api.post(`/admin/bookings/${b.id}/decline`);
      toast.success("Booking declined");
      refetch();
    } catch {
      toast.error("Could not decline");
    }
  };

  const exportCsv = () => {
    const headers = [
      "student_name", "parent_name", "parent_email", "parent_phone",
      "esa_contract_number", "subject", "service_type", "duration_minutes",
      "client_address", "status", "preferred_slots", "confirmed_slot",
      "travel_fee", "created_at",
    ];
    const rows = bookings.map((b) => [
      b.student_name, b.parent_name, b.parent_email, b.parent_phone,
      b.esa_contract_number, b.subject, b.service_type, b.duration_minutes,
      b.client_address || "", b.status,
      (b.preferred_slots || []).join(" | "), b.confirmed_slot_iso || "",
      b.travel_fee || "", b.created_at,
    ]);
    downloadCsv("bookings", headers, rows);
  };

  const headerBar = (
    <div className="flex justify-between items-center mb-4 gap-2">
      <button
        type="button"
        onClick={() => setAddingManual(true)}
        className="btn-pill btn-primary"
        data-testid="admin-add-manual-booking"
      >
        <Plus size={14} /> Add Booking
      </button>
      <button
        type="button"
        onClick={backfillInvoices}
        className="btn-pill btn-outline text-xs"
        title="Assign invoice numbers to older manual bookings created before the invoice fix"
        data-testid="admin-backfill-invoices"
      >
        <Wrench size={14} /> Backfill invoices
      </button>
      <button
        type="button"
        onClick={exportCsv}
        className="btn-pill btn-outline"
        data-testid="admin-bookings-export-csv"
      >
        <Download size={14} /> Export CSV
      </button>
    </div>
  );

  if (bookings.length === 0) {
    return (
      <>
        {headerBar}
        <RecurringPatternsPanel gcalConnected={gcalConnected} onMaterialized={refetch} />
        <div className="rounded-2xl border border-gg-border bg-gg-surface p-12 text-center text-gg-muted mt-4">
          No 1-on-1 booking requests yet.
        </div>
        {addingManual && (
          <ManualBookingDialog
            open={addingManual}
            onClose={() => setAddingManual(false)}
            onCreated={() => { setAddingManual(false); refetch(); }}
          />
        )}
      </>
    );
  }

  return (
    <>
      {headerBar}
      <RecurringPatternsPanel gcalConnected={gcalConnected} onMaterialized={refetch} />
      <div className="rounded-2xl border border-gg-border bg-gg-surface overflow-hidden mt-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gg-bg text-xs uppercase tracking-[0.14em] text-gg-brown">
              <tr>
                <Th>Student</Th>
                <Th>Parent</Th>
                <Th>Preferred / Confirmed</Th>
                <Th>Status</Th>
                <Th>Calendar</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} className="border-t border-gg-border align-top" data-testid={`admin-booking-row-${b.id}`}>
                  <Td>
                    <div className="font-semibold">{b.student_name}</div>
                    <div className="text-xs text-gg-muted">ESA #{b.esa_contract_number}</div>
                  </Td>
                  <Td>
                    <div>{b.parent_name}</div>
                    <div className="text-xs text-gg-muted">{b.parent_email}</div>
                    <div className="text-xs text-gg-muted">{b.parent_phone}</div>
                  </Td>
                  <Td>
                    {b.status === "confirmed" ? (
                      <div className="text-sm">
                        <CheckCircle2 size={14} className="inline text-gg-green mr-1" />
                        {new Date(b.confirmed_start_iso).toLocaleString("en-US", {
                          weekday: "short", month: "short", day: "numeric",
                          hour: "numeric", minute: "2-digit", timeZone: "America/Phoenix",
                        })}
                      </div>
                    ) : (
                      <ul className="space-y-1 text-xs">
                        {(b.preferred_slots || []).map((s, i) => (
                          <li key={i} className="text-gg-muted">{s.label}</li>
                        ))}
                      </ul>
                    )}
                    {b.subject && (
                      <div className="text-xs text-gg-brown font-semibold mt-2 max-w-xs">
                        Subject: <span className="font-normal text-gg-muted">{b.subject}</span>
                      </div>
                    )}
                    {b.notes && (
                      <div className="text-xs text-gg-muted mt-2 italic max-w-xs">&quot;{b.notes}&quot;</div>
                    )}
                  </Td>
                  <Td>
                    <StatusBadge status={b.status} />
                  </Td>
                  <Td>
                    {b.calendar_event_link ? (
                      <a href={b.calendar_event_link} target="_blank" rel="noreferrer" className="text-xs text-gg-green hover:underline inline-flex items-center gap-1">
                        <Calendar size={12} /> View event
                      </a>
                    ) : b.calendar_error ? (
                      <span className="text-xs text-[#B24C4C]" title={b.calendar_error}>error</span>
                    ) : (
                      <span className="text-xs text-gg-muted">—</span>
                    )}
                  </Td>
                  <Td>
                    {b.status === "requested" && (
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => setConfirming(b)}
                          className="text-xs px-3 py-1.5 rounded-full bg-gg-green/10 text-gg-green hover:bg-gg-green hover:text-white transition-colors"
                          data-testid={`admin-confirm-booking-${b.id}`}
                        >
                          Confirm
                        </button>
                        <button
                          type="button"
                          onClick={() => decline(b)}
                          className="text-xs px-3 py-1.5 rounded-full bg-gg-bg border border-gg-border text-gg-brown hover:bg-[#B24C4C] hover:text-white hover:border-[#B24C4C] transition-colors"
                          data-testid={`admin-decline-booking-${b.id}`}
                        >
                          Decline
                        </button>
                      </div>
                    )}
                    {b.status === "confirmed" && b.invoice_number && (
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const { data } = await api.get(`/admin/bookings/${b.id}/invoice-url`);
                            window.open(`${API}${data.url}`, "_blank", "noopener");
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || "Could not open invoice");
                          }
                        }}
                        className="text-xs px-3 py-1.5 rounded-full bg-gg-brown/10 text-gg-brown hover:bg-gg-brown hover:text-white transition-colors inline-block"
                        data-testid={`admin-booking-invoice-${b.id}`}
                      >
                        Invoice #{b.invoice_number}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onEdit?.(b)}
                      className="text-xs px-3 py-1.5 rounded-full bg-gg-bg border border-gg-border text-gg-brown hover:border-gg-green hover:text-gg-green transition-colors mt-1 inline-flex items-center gap-1"
                      data-testid={`admin-edit-booking-${b.id}`}
                    >
                      <Pencil size={12} /> Edit
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {confirming && (
        <ConfirmBookingDialog
          booking={confirming}
          gcalConnected={gcalConnected}
          onClose={() => setConfirming(null)}
          onConfirmed={() => {
            setConfirming(null);
            refetch();
          }}
        />
      )}
      {addingManual && (
        <ManualBookingDialog
          open={addingManual}
          onClose={() => setAddingManual(false)}
          onCreated={() => { setAddingManual(false); refetch(); }}
        />
      )}
    </>
  );
}

function ConfirmBookingDialog({ booking, gcalConnected, onClose, onConfirmed }) {
  const initial = booking.preferred_slots?.[0];
  const initialDateTime = initial ? initial.iso_datetime.slice(0, 16) : "";
  const [dt, setDt] = useState(initialDateTime);
  const [createEvent, setCreateEvent] = useState(true);
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    if (!dt) {
      toast.error("Pick a date and time");
      return;
    }
    setBusy(true);
    try {
      const iso = `${dt}:00-07:00`;
      const { data } = await api.post(`/admin/bookings/${booking.id}/confirm`, {
        confirmed_start_iso: iso,
        create_calendar_event: createEvent,
      });
      if (data.calendar_error) {
        toast.warning(`Booking confirmed — calendar event failed: ${data.calendar_error}`);
      } else if (data.calendar_event_link) {
        toast.success("Booking confirmed + calendar event created");
      } else {
        toast.success("Booking confirmed");
      }
      onConfirmed();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not confirm";
      toast.error(typeof detail === "string" ? detail : "Could not confirm");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-gg-surface max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Confirm booking</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-sm">
            <p className="font-semibold">{booking.student_name}</p>
            <p className="text-gg-muted">Parent: {booking.parent_name} · {booking.parent_email}</p>
          </div>

          {booking.preferred_slots?.length > 0 && (
            <div className="rounded-xl bg-gg-bg border border-gg-border p-3">
              <p className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold mb-2">
                Parent&apos;s suggestions
              </p>
              <div className="flex flex-wrap gap-2">
                {booking.preferred_slots.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setDt(s.iso_datetime.slice(0, 16))}
                    className="text-xs px-2.5 py-1 rounded-full bg-gg-surface border border-gg-border hover:border-gg-green hover:text-gg-green transition-colors"
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <Label className="block">
            <span className="block text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold mb-2">
              Final date &amp; time (Arizona)
            </span>
            <Input type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} data-testid="confirm-booking-datetime" />
            <span className="block text-xs text-gg-muted mt-1.5">
              60-minute session · Google Calendar uses Arizona time (no DST)
            </span>
          </Label>

          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              checked={createEvent}
              onChange={(e) => setCreateEvent(e.target.checked)}
              className="mt-1"
              disabled={!gcalConnected}
            />
            <span>
              <strong className="block text-[var(--gg-text)]">Create Google Calendar event</strong>
              <span className="text-gg-muted">
                {gcalConnected
                  ? `Adds the session to your calendar and emails an invite to ${booking.parent_email}.`
                  : "Connect Google Calendar first to enable this (banner at top of page)."}
              </span>
            </span>
          </label>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className="btn-pill btn-outline">
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={busy}
            className="btn-pill btn-primary disabled:opacity-60"
            data-testid="confirm-booking-save"
          >
            {busy ? "Confirming…" : "Confirm booking"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
